import { useState, useEffect, useRef } from "react"
import {
  Crown, Zap, Copy, Check, Pen, Loader2, Camera,
  type LucideIcon, Sprout, Wrench, Brain, Flame, Phone,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { CommunityAvatar } from "@/src/components/community/community-avatar"
import { AvatarUploadModal } from "./avatar-upload-modal"
import type { CommunityUser } from "@/lib/community/types"
import { CREATOR_LEVELS, CREATOR_LEVEL_VISUALS } from "@/lib/community/types"
import { updateNickname, ApiError } from "@/lib/api"

import { toggleFollow } from "@/lib/community/service/interaction-service"
import "./profile-page.less"
import { useAuthContext } from "@/lib/auth-context"
import { useToast } from "@/lib/hooks/use-toast"

const LEVEL_ICONS: Record<string, LucideIcon> = {
  seedling: Sprout, wrench: Wrench, zap: Zap,
  brain: Brain, flame: Flame, crown: Crown,
}

/* Lighter gradient backgrounds — raised lightness from 0.28-0.35 → 0.82-0.90 */
const LEVEL_GRADIENTS: Record<number, string> = {
  1: "linear-gradient(135deg, oklch(0.92 0.03 160), oklch(0.86 0.05 180))",
  2: "linear-gradient(135deg, oklch(0.88 0.08 240), oklch(0.82 0.10 260))",
  3: "linear-gradient(135deg, oklch(0.88 0.08 160), oklch(0.82 0.10 175))",
  4: "linear-gradient(135deg, oklch(0.88 0.08 65), oklch(0.82 0.10 45))",
  5: "linear-gradient(135deg, oklch(0.86 0.10 25), oklch(0.80 0.12 10))",
  6: "linear-gradient(135deg, oklch(0.88 0.10 70), oklch(0.82 0.12 30), oklch(0.85 0.08 280))",
}

interface ProfileHeroSectionProps {
  user: CommunityUser
  isOwnProfile: boolean
  onOpenBadgeWall: () => void
  onOpenFollowing?: () => void
  onUserUpdate?: (patch: Partial<CommunityUser>) => void
  onOpenVip?: () => void
  boundPhone?: string | null
  onOpenBindPhone?: () => void
}

export function ProfileHeroSection({
  user,
  isOwnProfile,
  onOpenBadgeWall,
  onOpenFollowing,
  onUserUpdate,
  onOpenVip,
  boundPhone,
  onOpenBindPhone,
}: ProfileHeroSectionProps) {
  const [copied, setCopied] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const isFollowing = user.isFollowed ?? false
  const [followLoading, setFollowLoading] = useState(false)
  const [followHover, setFollowHover] = useState(false)
  const [showAvatarModal, setShowAvatarModal] = useState(false)

  // Inline nickname editing
  const { refreshAvatar, isLoggedIn, openLogin } = useAuthContext()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [nickname, setNickname] = useState(user.displayName)
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const levelInfo = CREATOR_LEVELS[user.creatorLevel - 1]
  const visualInfo = CREATOR_LEVEL_VISUALS[user.creatorLevel - 1]
  const LevelIcon = LEVEL_ICONS[levelInfo.icon] ?? Sprout
  const maskedPhone = boundPhone && boundPhone.length >= 7
    ? `${boundPhone.slice(0, 3)}******${boundPhone.slice(-2)}`
    : boundPhone

  useEffect(() => {
    setIsVisible(true)
  }, [])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const copyLink = () => {
    navigator.clipboard.writeText(`safeclaw.ai/@${user.handle ?? user.id}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSaveNickname = async () => {
    const trimmed = nickname.trim()
    if (!trimmed || trimmed === user.displayName) {
      setNickname(user.displayName)
      setEditing(false)
      return
    }
    setSaving(true)
    setEditError("")
    try {
      await updateNickname(trimmed)
      onUserUpdate?.({ displayName: trimmed })
      setEditing(false)
    } catch (err) {
      setEditError(err instanceof ApiError ? (err.statusText || "修改失败") : "网络异常")
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); handleSaveNickname() }
    else if (e.key === "Escape") { setNickname(user.displayName); setEditing(false) }
  }

  const handleFollow = async () => {
    if (!isLoggedIn) { openLogin(); return }
    if (followLoading) return
    setFollowLoading(true)
    try {
      const nowFollowed = await toggleFollow(user.id)
      toast.success(nowFollowed ? "关注成功" : "已取消关注")
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
      toast.error(err instanceof ApiError ? err.statusText : "操作失败，请重试")
    } finally {
      setFollowLoading(false)
    }
  }

  return (
    <section className="profile-hero-card relative overflow-hidden">
      {/* Background: cover image + overlay OR light level gradient */}
      {user.coverImage ? (
        <>
          <img
            src={user.coverImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(
                to bottom,
                rgba(255,255,255,0.1) 0%,
                rgba(255,255,255,0.3) 50%,
                rgba(255,255,255,0.6) 100%
              )`,
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: LEVEL_GRADIENTS[user.creatorLevel] ?? LEVEL_GRADIENTS[1] }}
        />
      )}

      {/* Share button — top right */}
      <div className="absolute top-4 right-4 z-20">
        <button
          type="button"
          onClick={copyLink}
          className="profile-icon-action"
          aria-label={copied ? "已复制主页链接" : "分享主页"}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      {/* ── Profile header content ── */}
      <div className={cn(
        "relative z-10 profile-hero-content transition-all duration-700",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}>

        {/* Avatar */}
        <div className="relative group profile-hero-avatar">
          <div
            className={cn(
              "relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden profile-avatar-glow shrink-0",
              visualInfo.ringClass,
            )}
            style={{ boxShadow: `0 0 30px ${visualInfo.glowColor}` }}
          >
            <CommunityAvatar
              src={user.avatar}
              name={user.displayName}
              sizeClass="w-full h-full"
            />
            {isOwnProfile && (
              <button
                type="button"
                onClick={() => setShowAvatarModal(true)}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/40 transition-all duration-200 cursor-pointer"
              >
                <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
          {/* Level ring expand for high levels */}
          {user.creatorLevel >= 4 && (
            <div className="absolute inset-0 rounded-full border-2 pointer-events-none profile-ring-expand"
              style={{ borderColor: `${levelInfo.color}80` }}
            />
          )}
          {/* Verified badge */}
          {user.isVerified && (
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center"
              style={{ boxShadow: `0 0 12px oklch(var(--primary) / 0.5)` }}
            >
              <Zap className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
          )}
        </div>

        <div className="profile-hero-main">
          {/* Display name */}
          <div className="profile-hero-title-row">
            {editing ? (
              <div className="flex items-center gap-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={nickname}
                  onChange={(e) => { setNickname(e.target.value); setEditError("") }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveNickname}
                  disabled={saving}
                  className="profile-hero-display-name-input"
                />
                {saving && <Loader2 className="h-5 w-5 animate-spin text-foreground/40" />}
              </div>
            ) : (
              <h1 className="profile-hero-display-name-light">
                {user.displayName}
              </h1>
            )}
            {editError && <p className="text-sm text-red-500 mt-1">{editError}</p>}
          </div>

          {/* Handle */}
          {(user.handle || isOwnProfile) && (
            <div className="profile-hero-meta-row">
              {user.handle && (
                <span className="text-foreground/40 text-xs sm:text-sm">@{user.handle}</span>
              )}
              {isOwnProfile && (
                maskedPhone ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <Phone className="h-3 w-3" />
                    已绑定
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={onOpenBindPhone}
                    className="inline-flex items-center gap-1 rounded-full border border-foreground/15 px-2.5 py-1 text-xs font-medium text-foreground/60 transition-all hover:border-foreground/25 hover:bg-foreground/5 hover:text-foreground"
                  >
                    <Phone className="h-3 w-3" />
                    绑定手机号
                  </button>
                )
              )}
            </div>
          )}

          {/* Level badge */}
          <button
            type="button"
            onClick={onOpenBadgeWall}
            className={cn(
              "profile-level-chip",
              user.creatorLevel >= 5
                ? "bg-primary/10 text-primary profile-level-badge"
                : "bg-foreground/5 text-foreground/70"
            )}
          >
            <LevelIcon className="w-3.5 h-3.5" />
            <span>Lv.{user.creatorLevel}</span>
            <span className="opacity-60">{levelInfo.title}</span>
          </button>

          {user.bio && (
            <p className="profile-hero-subline">
              {user.bio}
            </p>
          )}

          {/* CTA — edit or follow */}
          <div className="profile-hero-actions">
            {isOwnProfile ? (
              <>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="profile-secondary-action"
                >
                  <Pen className="w-3.5 h-3.5" />
                  编辑主页
                </button>
              {/* {!user.vip?.isVip && (
                <button
                  type="button"
                  onClick={onOpenVip}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all hover:scale-105 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white shadow-lg shadow-amber-500/20"
                >
                  <Crown className="w-3.5 h-3.5" />
                  开通 VIP
                </button>
              )}
              {user.vip?.isVip && (
                <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <Crown className="w-3.5 h-3.5" />
                  VIP 会员
                </span>
              )} */}
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={followLoading}
                  onMouseEnter={() => setFollowHover(true)}
                  onMouseLeave={() => setFollowHover(false)}
                  onClick={handleFollow}
                  className={cn(
                    "profile-primary-action",
                    isFollowing
                      ? followHover
                        ? "border border-red-400/50 text-red-500 bg-background/70"
                        : "border border-foreground/15 text-foreground/70 bg-background/70"
                      : "bg-primary text-primary-foreground hover:bg-primary/90",
                    followLoading && "opacity-60 pointer-events-none"
                  )}
                >
                  {isFollowing ? (followHover ? "取消关注" : "已关注") : "关注"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px profile-hero-line" />

      {/* Avatar upload modal */}
      {showAvatarModal && (
        <AvatarUploadModal
          currentAvatar={user.avatar}
          onClose={() => setShowAvatarModal(false)}
          onSuccess={(avatarUrl) => {
            onUserUpdate?.({ avatar: avatarUrl })
            refreshAvatar()
            setShowAvatarModal(false)
          }}
        />
      )}
    </section>
  )
}
